/**
 * Platform Targeting Configuration
 *
 * Defines which notifications are sent to which user roles and platforms.
 * This is the source of truth for notification routing decisions.
 */

import type { NotificationChannel, PreferenceKey } from "./channels";
import { NotificationChannels, PreferenceKeys } from "./channels";
import type { NotificationType } from "./types";
import { NotificationTypes } from "./types";

// ============================================================================
// User Role Targeting
// ============================================================================

export const UserRoles = {
  PARTICIPANT: "participant",
  RESEARCHER: "researcher",
  BOTH: "both",
} as const;

export type UserRole = (typeof UserRoles)[keyof typeof UserRoles];

// ============================================================================
// Platform Support
// ============================================================================

export const Platforms = {
  WEB: "web",
  IOS: "ios",
  ANDROID: "android",
} as const;

export type Platform = (typeof Platforms)[keyof typeof Platforms];

// ============================================================================
// Notification Targeting Configuration
// ============================================================================

export interface NotificationTargeting {
  /** Who receives this notification */
  targetRole: UserRole;
  /** Platforms where this notification is supported */
  supportedPlatforms: Platform[];
  /**
   * All channels that exist for this notification type (for documentation).
   * Note: IN_APP is always enabled - users cannot disable in-app notifications.
   * Only external channels (EMAIL, PUSH) are user-controllable.
   * PHONE_CALL is planned but not yet enabled.
   */
  availableChannels: NotificationChannel[];
  /**
   * Default enabled channels for new users (includes IN_APP for reference).
   * Note: User preferences only store external channels (excluding IN_APP).
   */
  defaultEnabledChannels: NotificationChannel[];
  /** Whether push/iOS notifications are useful for this type */
  pushRecommended: boolean;
  /** Priority for sorting in UI */
  uiPriority: number;
  /** Display name for UI */
  displayName: string;
  /** Description for UI */
  description: string;
}

// ============================================================================
// Targeting Configuration by Notification Type
// ============================================================================

/**
 * Email-only lifecycle notification targeting (participant-facing, no push).
 */
function lifecycleEmailTargeting(
  displayName: string,
  description: string,
): NotificationTargeting {
  return {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 5,
    displayName,
    description,
  };
}

/**
 * Configuration for each notification type
 */
export const notificationTargeting: Record<
  NotificationType,
  NotificationTargeting
> = {
  // ===== PARTICIPANT-FACING NOTIFICATIONS =====
  // These are sent to participants and benefit from iOS/push notifications

  [NotificationTypes.INTERVIEW_INVITATION_SENT]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 1,
    displayName: "Interview invitations",
    description: "Get notified when you're invited to participate in a study",
  },

  [NotificationTypes.INTERVIEW_EARNINGS_POSTED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 3,
    displayName: "Earnings posted",
    description: "Get notified when you earn money from completing interviews",
  },

  [NotificationTypes.INTERVIEW_APPROVED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 4,
    displayName: "Interview approved",
    description: "Notification when your interview submission is approved",
  },

  [NotificationTypes.INTERVIEW_AUTO_APPROVED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 5,
    displayName: "Auto-approval notices",
    description: "Notification when your interview is automatically approved",
  },

  [NotificationTypes.INTERVIEW_REJECTED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 6,
    displayName: "Interview rejected",
    description: "Important alerts when an interview submission is rejected",
  },

  [NotificationTypes.PAYMENT_CLAWED_BACK]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 7,
    displayName: "Payment clawback",
    description: "Critical alert when a payment is reversed from your account",
  },

  [NotificationTypes.BALANCE_NEGATIVE]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 8,
    displayName: "Negative balance",
    description: "Critical alert when your account balance goes negative",
  },

  // ===== RESEARCHER-FACING NOTIFICATIONS =====
  // These are primarily for researchers and are less useful on iOS

  [NotificationTypes.BUDGET_THRESHOLD_REACHED]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB], // Web-only, not useful on mobile
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 1,
    displayName: "Budget threshold alerts",
    description: "Get alerts when a study reaches 50%, 80%, and 100% of budget",
  },

  [NotificationTypes.STUDY_APPROVAL_REQUESTED]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 2,
    displayName: "Approval requests",
    description: "Notifications when a study needs your approval",
  },

  [NotificationTypes.STUDY_APPROVAL_DECIDED]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 3,
    displayName: "Approval decisions",
    description: "Updates when approval requests are decided",
  },

  [NotificationTypes.STUDY_STARTED]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    pushRecommended: false,
    uiPriority: 4,
    displayName: "Study started",
    description: "Notification when a study begins collecting responses",
  },

  [NotificationTypes.STUDY_ETA_SET]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [NotificationChannels.IN_APP],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    pushRecommended: false,
    uiPriority: 4,
    displayName: "Delivery turnaround set",
    description:
      "Notification when a delivery turnaround is first set for your study",
  },

  [NotificationTypes.STUDY_ETA_UPDATED]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [NotificationChannels.IN_APP],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    pushRecommended: false,
    uiPriority: 4,
    displayName: "Delivery turnaround updated",
    description:
      "Notification when a delivery turnaround is changed for your study",
  },

  [NotificationTypes.FEASIBILITY_QUOTE_READY]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 4,
    displayName: "Quote ready",
    description:
      "Notification when Terac has priced your study and it is ready to review",
  },

  // ===== SHARED NOTIFICATIONS =====
  // These apply to both roles

  [NotificationTypes.ORGANIZATION_INVITATION_CREATED]: {
    targetRole: UserRoles.BOTH,
    supportedPlatforms: [Platforms.WEB], // No mobile - researcher-facing
    availableChannels: [NotificationChannels.IN_APP],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    pushRecommended: false,
    uiPriority: 1,
    displayName: "Organization invitations",
    description: "Get notified when you're invited to join an organization",
  },

  [NotificationTypes.ORGANIZATION_INVITATION_ACCEPTED]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    pushRecommended: false,
    uiPriority: 2,
    displayName: "Invitation accepted",
    description: "Notification when someone accepts your organization invite",
  },

  [NotificationTypes.ORGANIZATION_INVITATION_REVOKED]: {
    targetRole: UserRoles.BOTH,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    pushRecommended: false,
    uiPriority: 3,
    displayName: "Invitation revoked",
    description: "Notification when an organization invitation is withdrawn",
  },

  // ===== PANEL ENGAGEMENT NOTIFICATIONS =====
  // These are automated emails to keep panel members engaged

  [NotificationTypes.INVITATION_REMINDER]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 9,
    displayName: "Invitation reminders",
    description: "Reminder about pending study invitations",
  },

  [NotificationTypes.INVITATION_REOPENED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 8,
    displayName: "Application re-opened",
    description:
      "Researcher re-opened your application after a previous rejection",
  },

  [NotificationTypes.POST_FIRST_INTERVIEW]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 10,
    displayName: "First interview follow-up",
    description: "Thank you message after completing your first interview",
  },

  [NotificationTypes.INVITATION_FEEDBACK_REQUEST]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [NotificationChannels.EMAIL],
    pushRecommended: false,
    uiPriority: 12,
    displayName: "Feedback requests",
    description: "Quick questions about declined invitations",
  },

  [NotificationTypes.POWER_USER_NPS]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [NotificationChannels.EMAIL],
    pushRecommended: false,
    uiPriority: 13,
    displayName: "Feedback surveys",
    description: "Occasional surveys about your experience",
  },

  // ===== STUDY HEALTH ALERTS (RESEARCHER) =====

  [NotificationTypes.STUDY_STUCK]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 3,
    displayName: "Study stuck alert",
    description:
      "Get notified when a study has no approved responses and a high screen-out rate",
  },

  [NotificationTypes.STUDY_FULFILLMENT_ALERT]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 3,
    displayName: "Fulfillment alert",
    description:
      "Get notified when a study is approaching, exceeding, or slow to reach its target",
  },

  [NotificationTypes.STUDY_BOOKING_NEEDED]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 3,
    displayName: "Booking needed",
    description:
      "Get notified when experts have shared their calendars and are waiting for you to book interview times",
  },

  [NotificationTypes.RESEARCHER_REVIEW_DIGEST]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 2,
    displayName: "Review reminders",
    description:
      "Grouped reminders for applicants and completed work waiting for your review",
  },

  // Both sides, because the Google event reaches neither reliably: the expert
  // owns it, so Google invites them to nothing, and the research team rides on
  // an invitation that needs a known address and an insert Google accepted.
  // Keyless (see notificationTypeToPreferenceKey), so it appears in no
  // preference group and cannot be switched off.
  [NotificationTypes.INTERVIEW_BOOKED]: {
    targetRole: UserRoles.BOTH,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    // No PUSH channel is wired for this type, so recommending it here would
    // only contradict availableChannels above.
    pushRecommended: false,
    uiPriority: 1,
    displayName: "Interview booked",
    description: "The date, time and join link for a scheduled interview",
  },

  // Expert-only: the researcher is a Google attendee with sendUpdates "all" and
  // already gets Google's reminders, and the org-wide owner/admin fan-out that
  // INTERVIEW_BOOKED uses would be spam twice per booking. Keyless (see
  // notificationTypeToPreferenceKey).
  [NotificationTypes.INTERVIEW_REMINDER]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 1,
    displayName: "Interview reminder",
    description:
      "A nudge on the day of a scheduled interview, and an hour before",
  },

  // EMAIL only, and no preference key: this is an operational message about a
  // study the recipient applied to, sent by a person, and it carries no in-app
  // counterpart worth surfacing.
  [NotificationTypes.STUDY_EMAIL_BLAST]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [NotificationChannels.EMAIL],
    defaultEnabledChannels: [NotificationChannels.EMAIL],
    pushRecommended: false,
    uiPriority: 4,
    displayName: "Message from the study team",
    description:
      "Receive messages the research team sends about a study you applied to",
  },

  // The signed contract itself. Keyless (see notificationTypeToPreferenceKey):
  // this is the expert's only copy of an agreement they are a party to, and a
  // stored preference must not be able to withhold it.
  [NotificationTypes.CONTRACT_SIGNED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 1,
    displayName: "Signed contract",
    description: "Your copy of a contract you signed on an opportunity",
  },

  // A study's office hours as a calendar invitation. Keyless for the reason
  // CONTRACT_SIGNED is: it is the only carrier of the invitation, and a
  // withheld update or cancellation leaves a recurring entry on the expert's
  // calendar that nothing else can correct.
  [NotificationTypes.OFFICE_HOURS_ANNOUNCED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 2,
    displayName: "Office hours",
    description:
      "A calendar invite for a study's office hours, and any change to them",
  },

  // Keyless for the reason CONTRACT_SIGNED is: nothing else tells an expert
  // that the project they were working on has ended for them.
  [NotificationTypes.PROJECT_OFFBOARDED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 1,
    displayName: "Offboarded from a project",
    description: "When you are taken off a project you were working on",
  },

  // Keyless for the same reason: nothing else tells an expert that a project
  // they were on has ended.
  [NotificationTypes.PROJECT_CLOSED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 1,
    displayName: "Project ended",
    description: "When a project you are on ends",
  },

  [NotificationTypes.PLAN_REMINDER]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 4,
    displayName: "Plan your hours",
    description:
      "A weekly nudge when you worked last week and have not planned the next",
  },

  // ===== SUPPORT NOTIFICATIONS =====
  // System-sent, not user-controllable

  [NotificationTypes.SUPPORT_AUTO_RESPONSE]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [NotificationChannels.EMAIL],
    defaultEnabledChannels: [NotificationChannels.EMAIL],
    pushRecommended: false,
    uiPriority: 99,
    displayName: "Support auto-response",
    description: "Automatic acknowledgement of support emails",
  },

  // ===== SYSTEM NOTIFICATIONS =====

  [NotificationTypes.WELCOME]: {
    targetRole: UserRoles.BOTH,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [NotificationChannels.EMAIL],
    defaultEnabledChannels: [NotificationChannels.EMAIL],
    pushRecommended: false,
    uiPriority: 99,
    displayName: "Welcome email",
    description: "Welcome email sent after account creation",
  },

  // ===== REFERRAL OUTREACH =====

  [NotificationTypes.REFERRAL_OUTREACH_REQUEST]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 6,
    displayName: "Referral outreach",
    description:
      "Get notified when you can earn by referring someone to a study",
  },

  [NotificationTypes.POST_COMPLETION_REFERRAL]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 7,
    displayName: "Post-completion referral",
    description: "Referral suggestions sent after completing a study",
  },

  [NotificationTypes.CONNECTION_MATCH]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 7,
    displayName: "Connection matches",
    description:
      "Get notified when one of your connections fits a study you can refer them to",
  },

  [NotificationTypes.STUDY_COMPLETED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    pushRecommended: false,
    uiPriority: 11,
    displayName: "Study wrap-up follow-up",
    description:
      "Nudge when a study you applied to closes, pointing to other open opportunities",
  },

  [NotificationTypes.NEXT_TASK_AVAILABLE]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 3,
    displayName: "Next task available",
    description:
      "Get notified when the next part of a multi-part study is ready",
  },

  [NotificationTypes.TASK_APPROVED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    pushRecommended: true,
    uiPriority: 3,
    displayName: "Task approved",
    description:
      "Get notified when your task submission in a multi-part study is approved",
  },

  // ===== LIFECYCLE EMAILS =====
  // Email-only by default. Participants can disable in preferences.

  [NotificationTypes.WELCOME_NUDGE_FIRST]: lifecycleEmailTargeting(
    "Welcome nudge",
    "First nudge to start onboarding after signup",
  ),
  [NotificationTypes.WELCOME_NUDGE_LAST]: lifecycleEmailTargeting(
    "Welcome nudge: final",
    "Final nudge to start onboarding",
  ),
  [NotificationTypes.FINISH_PROFILE_FIRST]: lifecycleEmailTargeting(
    "Finish profile reminder",
    "Nudge to finish onboarding",
  ),
  [NotificationTypes.FINISH_PROFILE_LAST]: lifecycleEmailTargeting(
    "Finish profile final reminder",
    "Last nudge to finish onboarding",
  ),
  [NotificationTypes.ONBOARDING_CONFIRMED]: lifecycleEmailTargeting(
    "Onboarding confirmed",
    "Confirmation that onboarding is complete",
  ),
  [NotificationTypes.FIRST_OPPORTUNITY_AWAITING_REVIEW]:
    lifecycleEmailTargeting(
      "First opportunity in review",
      "We received your first application and our team is reviewing it",
    ),
  [NotificationTypes.FIRST_OPPORTUNITY_SCREEN_PASSED]: lifecycleEmailTargeting(
    "First opportunity screening passed",
    "You passed screening on your first opportunity",
  ),
  [NotificationTypes.OPPORTUNITY_SCREEN_PASSED]: lifecycleEmailTargeting(
    "Approved for an opportunity",
    "You passed screening and can start the task",
  ),
  [NotificationTypes.SCREENING_STEP_READY]: lifecycleEmailTargeting(
    "Another screening step is ready",
    "One more screening step is waiting on you before we can decide",
  ),
  [NotificationTypes.APPROVED_NOT_STARTED_FIRST]: lifecycleEmailTargeting(
    "Study approved: start reminder",
    "Reminder to start a study you were approved for",
  ),
  [NotificationTypes.APPROVED_NOT_STARTED_LAST]: lifecycleEmailTargeting(
    "Study approved: final reminder",
    "Last reminder to start a study you were approved for",
  ),
  [NotificationTypes.APPLIED_SCREENED_OUT]: lifecycleEmailTargeting(
    "Application screened out",
    "Notice that an application didn't pass screening",
  ),
  [NotificationTypes.APPLIED_NOT_SELECTED]: lifecycleEmailTargeting(
    "Application not selected",
    "Notice that an application wasn't selected for the cohort",
  ),
  [NotificationTypes.DORMANT_30_DAYS]: lifecycleEmailTargeting(
    "Come back: 30 days",
    "Reactivation email after 30 days of inactivity",
  ),
  [NotificationTypes.DORMANT_60_DAYS]: lifecycleEmailTargeting(
    "Come back: 60 days",
    "Reactivation email after 60 days of inactivity",
  ),
  [NotificationTypes.DORMANT_90_DAYS]: lifecycleEmailTargeting(
    "Come back: 90 days",
    "Reactivation email after 90 days of inactivity",
  ),
  [NotificationTypes.COMPLETE_MATCH_PROFILE]: lifecycleEmailTargeting(
    "Complete match profile",
    "Nudge for verified panelists to fill missing match-profile fields",
  ),

  // Explicit opt-in, OFF by default: unlike lifecycle emails we do not seed any
  // default-enabled channel. EMAIL is the only user-controllable channel;
  // in-app rides along once the user opts into the category.
  [NotificationTypes.OPPORTUNITY_LISTED]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [],
    pushRecommended: false,
    uiPriority: 5,
    displayName: "New opportunities to refer",
    description:
      "Email when a new opportunity is listed so referrers can share it",
  },
};

// ============================================================================
// Preference Key Targeting
// ============================================================================

/**
 * Configuration for each preference key (grouped notifications)
 *
 * Note: IN_APP is always enabled (industry standard) and is NOT user-controllable.
 * The availableChannels and defaultEnabledChannels here include IN_APP for
 * documentation purposes, but user preferences only store external delivery channels.
 */
export interface PreferenceTargeting {
  targetRole: UserRole;
  supportedPlatforms: Platform[];
  /** All channels including IN_APP (for documentation) */
  availableChannels: NotificationChannel[];
  /** Default enabled channels including IN_APP (for reference) */
  defaultEnabledChannels: NotificationChannel[];
  displayName: string;
  description: string;
  /** Associated notification types */
  notificationTypes: NotificationType[];
}

export const preferenceTargeting: Record<PreferenceKey, PreferenceTargeting> = {
  // ===== PARTICIPANT PREFERENCES =====
  [PreferenceKeys.SURVEY_SENT]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    displayName: "Interview invitations",
    description: "Get notified when you're invited to participate in a study",
    notificationTypes: [
      NotificationTypes.INTERVIEW_INVITATION_SENT,
      NotificationTypes.INVITATION_REMINDER,
      NotificationTypes.INVITATION_REOPENED,
      NotificationTypes.POST_FIRST_INTERVIEW,
      NotificationTypes.INVITATION_FEEDBACK_REQUEST,
      NotificationTypes.POWER_USER_NPS,
    ],
  },

  [PreferenceKeys.REFERRAL_OUTREACH]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    displayName: "Referral opportunities",
    description:
      "Get notified when you can earn by referring someone to a study",
    notificationTypes: [
      NotificationTypes.REFERRAL_OUTREACH_REQUEST,
      NotificationTypes.POST_COMPLETION_REFERRAL,
    ],
  },

  [PreferenceKeys.NEW_OPPORTUNITY_ALERTS]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    // Explicit opt-in: OFF by default. Unlike other categories we do NOT seed
    // the always-on IN_APP baseline here, because delivery (email + in-app) is
    // gated entirely on the user turning EMAIL on. EMAIL is the only
    // user-controllable channel (see availableChannelsPerPreference).
    defaultEnabledChannels: [],
    displayName: "New opportunities to refer",
    description:
      "Email me whenever a new opportunity is listed so I can refer people to it",
    notificationTypes: [
      NotificationTypes.OPPORTUNITY_LISTED,
      NotificationTypes.CONNECTION_MATCH,
    ],
  },

  [PreferenceKeys.EARNINGS_PAYOUT]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    displayName: "Earnings & reviews",
    description: "Earnings posted, interview approvals, and rejections",
    notificationTypes: [
      NotificationTypes.INTERVIEW_EARNINGS_POSTED,
      NotificationTypes.INTERVIEW_APPROVED,
      NotificationTypes.INTERVIEW_AUTO_APPROVED,
      NotificationTypes.INTERVIEW_REJECTED,
    ],
  },

  [PreferenceKeys.BALANCE_ALERTS]: {
    targetRole: UserRoles.PARTICIPANT,
    supportedPlatforms: [Platforms.WEB, Platforms.IOS, Platforms.ANDROID],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
      NotificationChannels.PUSH,
    ],
    displayName: "Balance alerts",
    description: "Critical alerts for negative balance or payment clawbacks",
    notificationTypes: [
      NotificationTypes.PAYMENT_CLAWED_BACK,
      NotificationTypes.BALANCE_NEGATIVE,
    ],
  },

  // ===== RESEARCHER PREFERENCES =====
  [PreferenceKeys.BUDGET_THRESHOLD]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    displayName: "Budget alerts",
    description: "Alerts when studies reach 50%, 80%, and 100% of budget",
    notificationTypes: [NotificationTypes.BUDGET_THRESHOLD_REACHED],
  },

  [PreferenceKeys.STUDY_MILESTONES]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    displayName: "Study milestones",
    description: "Notifications when studies start or reach milestones",
    notificationTypes: [
      NotificationTypes.STUDY_STARTED,
      NotificationTypes.STUDY_STUCK,
      NotificationTypes.STUDY_FULFILLMENT_ALERT,
      NotificationTypes.STUDY_ETA_SET,
      NotificationTypes.STUDY_ETA_UPDATED,
      NotificationTypes.FEASIBILITY_QUOTE_READY,
      NotificationTypes.STUDY_BOOKING_NEEDED,
      NotificationTypes.RESEARCHER_REVIEW_DIGEST,
    ],
  },

  [PreferenceKeys.STUDY_APPROVALS]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    displayName: "Approval workflow",
    description: "Approval requests and decisions for your studies",
    notificationTypes: [
      NotificationTypes.STUDY_APPROVAL_REQUESTED,
      NotificationTypes.STUDY_APPROVAL_DECIDED,
    ],
  },

  [PreferenceKeys.ORG_UPDATES]: {
    targetRole: UserRoles.RESEARCHER,
    supportedPlatforms: [Platforms.WEB],
    availableChannels: [
      NotificationChannels.EMAIL,
      NotificationChannels.IN_APP,
    ],
    defaultEnabledChannels: [NotificationChannels.IN_APP],
    displayName: "Organization updates",
    description: "Membership changes and organization activity",
    notificationTypes: [
      NotificationTypes.ORGANIZATION_INVITATION_ACCEPTED,
      NotificationTypes.ORGANIZATION_INVITATION_REVOKED,
    ],
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get preferences applicable to a user role
 */
export function getPreferencesForRole(
  role: "participant" | "researcher",
): PreferenceKey[] {
  return (Object.keys(preferenceTargeting) as PreferenceKey[]).filter((key) => {
    const targeting = preferenceTargeting[key];
    return (
      targeting.targetRole === role || targeting.targetRole === UserRoles.BOTH
    );
  });
}

/**
 * Check if a platform is supported for a notification type
 */
export function isPlatformSupported(
  notificationType: NotificationType,
  platform: Platform,
): boolean {
  const targeting = notificationTargeting[notificationType];
  return targeting.supportedPlatforms.includes(platform);
}

/**
 * Check if push notifications are recommended for a notification type
 */
export function isPushRecommended(notificationType: NotificationType): boolean {
  return notificationTargeting[notificationType].pushRecommended;
}

/**
 * Get notification types that support iOS push
 */
export function getIOSSupportedNotificationTypes(): NotificationType[] {
  return (Object.keys(notificationTargeting) as NotificationType[]).filter(
    (type) =>
      notificationTargeting[type].supportedPlatforms.includes(Platforms.IOS),
  );
}

/**
 * Get participant-facing preferences (for iOS settings)
 */
export function getParticipantPreferences(): PreferenceKey[] {
  return getPreferencesForRole("participant");
}

/**
 * Get researcher-facing preferences (web-only)
 */
export function getResearcherPreferences(): PreferenceKey[] {
  return (Object.keys(preferenceTargeting) as PreferenceKey[]).filter((key) => {
    const targeting = preferenceTargeting[key];
    return targeting.targetRole === UserRoles.RESEARCHER;
  });
}
